import type { LegacyRef } from 'react'
import type { ButtonCorners } from 'atoms/Button'
import type { TextBoxColors } from 'atoms/TextBox'
import type { CARDS_ASPECT_RATIO, EVENT_CARD_ASPECT_RATIO } from 'blocks/Cards'
import type { CARDS_ASPECT_RATIO_VARS } from 'vars/cards-ascpect-ratio'
import type { CAROUSEL_PADDINGS, GRID, ROW_GAP } from 'vars/index'

import * as Descriptions from 'blocks/Container/Description/Templates'
import {
  FIXED_INDENTS,
  INDENTS,
  LAYOUT,
  ROW_CONTAINER_VERTICAL_GAPS,
} from 'vars/index'

export interface IRowContainerProps {
  innerRef?: LegacyRef<HTMLDivElement>
  children?: React.ReactNode
  isMoreThanOnePage?: boolean
  rowScrollPosition?: number
  itemCount: number
  selectedItem?: number
  disableScrollOnSelectItem?: boolean

  cardsInRow?: CardsInRow
  scrollByCardsCount?: number
  gap?: ROW_GAP | GRID
  horizontalPaddings?: LAYOUT | FIXED_INDENTS | GRID
  verticalPaddingTop?: LAYOUT | FIXED_INDENTS | CAROUSEL_PADDINGS | GRID
  verticalMarginTop?: LAYOUT | FIXED_INDENTS | CAROUSEL_PADDINGS
  verticalMarginBottom?: LAYOUT | FIXED_INDENTS | CAROUSEL_PADDINGS
  verticalPaddingBottom?: LAYOUT | FIXED_INDENTS | CAROUSEL_PADDINGS | GRID
  cellsAlignment?: CellsAlignment
  tvChannelCard?: boolean
  cardAspectRatio?: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  cardAspectRatioVar?: CARDS_ASPECT_RATIO_VARS | EVENT_CARD_ASPECT_RATIO
  withCardDescription?: boolean
  /**
   * FP-4324: size in-row scroll controls to the poster box, not the full
   * card-plus-description height. Opt-in so other rows keep 101% / centered
   * arrows.
   */
  matchPosterHeight?: boolean
  titleColor?: TextBoxColors

  title?: string
  controlsType?: RowControlsType
  withSpecialCell?: boolean
  descriptionType?: RowDescriptionType
  description?: string
  buttonTitle?: string
  containerVerticalGap?: RowContainerVerticalGaps
  backgroundStyle?: SpecialRowContainerBackgroundT
  rowPosterImage?: string
  loaded?: boolean
  backgroundType?: 'UNSET' | 'IMAGE' | 'GRADIENT'
  backgroundGradient?: string

  onSeeAllClick?: () => void
  cornersButtonLeft?: ButtonCorners
  cornersButtonRight?: ButtonCorners
  visibleCards?: number
  isSpecialRow?: boolean
  isRowLoading?: boolean
  dataTestIdRow?: string
}

export const CellsAlignment = {
  Start: 'start',
  End: 'end',
  Center: 'center',
} as const
export type CellsAlignment =
  (typeof CellsAlignment)[keyof typeof CellsAlignment]

export const Gaps = {
  SIZE_0: FIXED_INDENTS.INDENT_0,
  SIZE_40: FIXED_INDENTS.INDENT_40,
}

export const RowPaddings = {
  None: [INDENTS.INDENT_0],
  DEFAULT: [LAYOUT.VerticalRowPaddings, LAYOUT.HorizontalPaddings],
  DEFAULT_CONTROLS: [FIXED_INDENTS.INDENT_60, LAYOUT.HorizontalPaddings],
}

export const RowControlsType = {
  inRowFluid: 'inRowFluid',
  inRow: 'inRow',
  inHeader: 'inHeader',
} as const
export type RowControlsType =
  (typeof RowControlsType)[keyof typeof RowControlsType]

export const CardsInRow = {
  Horizontal_2: 'Horizontal_2',
  Horizontal_3: 'Horizontal_3',
  Horizontal_4: 'Horizontal_4',
  Horizontal_5: 'Horizontal_5',
  Horizontal_6: 'Horizontal_6',
  Horizontal_7: 'Horizontal_7',
  Horizontal_8: 'Horizontal_8',
  Horizontal_9: 'Horizontal_9',
  Vertical_4: 'Vertical_4',
  Vertical_5: 'Vertical_5',
  Vertical_6: 'Vertical_6',
  Vertical_7: 'Vertical_7',
} as const
export type CardsInRow = (typeof CardsInRow)[keyof typeof CardsInRow]

export const RowAspectRatio = {
  VERTICAL_DEFAULT: 0.74,
  SQUARE: 1,
  HORIZONTAL_TV_SHOW_OUTER_CARD: 1.265,
  HORIZONTAL_DEFAULT: 1.48,
  HORIZONTAL_TV_SHOW_INNER_CARD: 1.781,
}

export const ScaleCoef = {
  Horizontal_2: 1.75,
  Horizontal_3: 1.5,
  Horizontal_4: 1.25,
  Horizontal_5: 1,
  Vertical_4: 1.75,
  Vertical_5: 1.5,
  Vertical_6: 1.25,
  Vertical_7: 1,
}

export const BannerWidth = {
  Banner_100: 100,
  Banner_92: 92,
  Banner_66: 66,
} as const
export type BannerWidth = (typeof BannerWidth)[keyof typeof BannerWidth]

export const BannerRowAspectRatio = {
  Vertical: 0.66,
  Horizontal_TV_Show: 1.77,
  Horizontal: 2.433,
  BannerSkeleton: 2.2,
} as const
export type BannerRowAspectRatio =
  (typeof BannerRowAspectRatio)[keyof typeof BannerRowAspectRatio]

export type SpecialRowContainerBackgroundT =
  | 'none'
  | 'Gradient'
  | 'GradientWithOverlay'
  | 'FullWidthImage'
  | 'HalfWidthImage'
  | 'SimpleFullWitdhImage'
  | 'SimpleGradient'
  | 'ConstantDark'

export const SpecialRowContainerBackground: Record<
  SpecialRowContainerBackgroundT,
  SpecialRowContainerBackgroundT
> = {
  none: 'none',
  Gradient: 'Gradient',
  GradientWithOverlay: 'GradientWithOverlay',
  FullWidthImage: 'FullWidthImage',
  HalfWidthImage: 'HalfWidthImage',
  SimpleFullWitdhImage: 'SimpleFullWitdhImage',
  SimpleGradient: 'SimpleGradient',
  ConstantDark: 'ConstantDark',
}

export const RowDescriptionType = {
  Base: 'Base',
  BaseWithInlineBtn: 'BaseWithInlineBtn',
  Centered: 'Centered',
  CenteredWithInlineBtn: 'CenteredWithInlineBtn',
} as const
export type RowDescriptionType =
  (typeof RowDescriptionType)[keyof typeof RowDescriptionType]

export const RowDescriptionsMap = {
  Base: Descriptions.ContainerDescriptionForSpecialRow,
  BaseWithInlineBtn:
    Descriptions.ContainerDescriptionForSpecialRowWithAngleButton,
  Centered: Descriptions.ContainerDescriptionForSpecialRowCenter,
  CenteredWithInlineBtn:
    Descriptions.ContainerDescriptionForSpecialRowWithAngleButtonCenter,
}

export const DEFAULT_PAGE_SIZE = 36

export const SwipeDirections = {
  Horizontal: 'horizontal',
  Vertical: 'vertical',
} as const
export type SwipeDirections =
  (typeof SwipeDirections)[keyof typeof SwipeDirections]

export const RowContainerVerticalGaps = {
  RowWithoutHeader: ROW_CONTAINER_VERTICAL_GAPS.RowWithoutHeader,
  RowDefault: ROW_CONTAINER_VERTICAL_GAPS.RowDefault,
  SpecialRowWithHeader: ROW_CONTAINER_VERTICAL_GAPS.SpecialRowWithHeaderDefault,
} as const
export type RowContainerVerticalGaps =
  (typeof RowContainerVerticalGaps)[keyof typeof RowContainerVerticalGaps]

export const ScrollBehavior = {
  smooth: 'smooth',
  auto: 'auto',
} as const
export type ScrollBehavior =
  (typeof ScrollBehavior)[keyof typeof ScrollBehavior]

export const ScrollAligning = {
  start: 'start',
  center: 'center',
  nearest: 'nearest',
  end: 'end',
} as const
export type ScrollAligning =
  (typeof ScrollAligning)[keyof typeof ScrollAligning]

export const SnapAlign = {
  start: 'start',
  center: 'center',
  end: 'end',
  unset: 'unset',
} as const
export type SnapAlign = (typeof SnapAlign)[keyof typeof SnapAlign]

export type ISnapAlign = keyof typeof SnapAlign
